import React, { useRef } from "react";

import "./styles.scss";
import FileIcon from "../../../../../../Components/Icon-SVG/FileIcon";
import AttackMapFolderIcon from "../../../../../../Components/Icon-SVG/AttackMapFolderIcon";

export const ENGINES = {
  yara: { id: "Yara", value: "Yara", color: "rgba(75, 192, 192, 0.8)" },
  sigma: { id: "sigma", value: "Sigma", color: "rgba(255, 206, 86, 0.8)" },
  cav: { id: "CAV", value: "CAV", color: "rgba(255, 99, 132, 0.8)" },
};

function FilterIcon({ size = 18 }) {
  return (
    <svg
      className="thunter-additional__filter-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true">
      <path
        d="M3 5h18M6 12h12M10 19h4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EngineBadge({ engine }) {
  const key = Object.keys(ENGINES).find(
    (k) => k.toLowerCase() === String(engine || "").toLowerCase(),
  );
  const cfg = ENGINES[key] || { id: engine, value: engine, color: "rgba(156,163,175,0.3)" };
  return (
    <span
      className="engine-badge"
      style={{ backgroundColor: cfg.color }}
      aria-label={`Engine: ${cfg.value}`}>
      {cfg.value}
    </span>
  );
}

function Pill({ children, kind = "neutral" }) {
  return <span className={`th-additional__pill th-additional__pill--${kind}`}>{children}</span>;
}

function SignatureBadge({ status, subject, issuer, hideWhenUnknown = true }) {
  const normalized = String(status || "").toLowerCase();
  const MAP = {
    trusted: { label: "Trusted", bg: "#388e3c" },
    untrusted: { label: "Untrusted", bg: "#d32f2f" },
    missing: { label: "Missing", bg: "#f57c00" },
    unknown: { label: "Unknown", bg: "rgba(156, 163, 175, 0.4)" },
    "": { label: "Unknown", bg: "rgba(156, 163, 175, 0.4)" },
  };
  const { label, bg } = MAP[normalized] || MAP.unknown;
  if (hideWhenUnknown && (normalized === "" || normalized === "unknown")) return null;
  const hasDetails = !!(subject || issuer);
  return (
    <span className="signature-badge-wrapper">
      <span
        className={`signature-badge signature-badge--${normalized || "unknown"}`}
        style={{ backgroundColor: bg }}>
        <span className="signature-badge__text">
          <span className="signature-badge__prefix">Sig:</span> {label}
        </span>
      </span>
      {hasDetails && (
        <span className="hovercard border" role="dialog">
          <div className="hovercard__title">Signature</div>
          <div className="hovercard__kv">
            <div className="hovercard__k">Status</div>
            <div className="hovercard__v">{label}</div>
          </div>
          {subject && (
            <div className="hovercard__kv">
              <div className="hovercard__k">Subject</div>
              <div className="hovercard__v" title={subject}>
                {subject}
              </div>
            </div>
          )}
          {issuer && (
            <div className="hovercard__kv">
              <div className="hovercard__k">Issuer</div>
              <div className="hovercard__v" title={issuer}>
                {issuer}
              </div>
            </div>
          )}
        </span>
      )}
    </span>
  );
}

function KeyValueRow({ label, value, mono = false, wrap = false }) {
  return (
    <div
      className={`thunter-additional-data__content__kv ${
        mono ? "thunter-additional-data__content__kv--mono" : ""
      } ${wrap ? "thunter-additional-data__content__kv--wrap" : ""}`}
      title={typeof value === "string" ? value : undefined}>
      <span className="thunter-additional-data__content__kv-label">{label}:</span>
      <span className="thunter-additional-data__content__kv-value">{value || ""}</span>
    </div>
  );
}

export default function Content({ item, preview, rightHeader, type, children }) {
  const isProcess = !!item?.process || !!item?.pid;
  const title = item?.file || item?.process || item?.path || "(unknown)";

  const hashes = item?.hashes || item || {};
  const md5 = hashes.md5 || "";
  const sha1 = hashes.sha1 || "";
  const sha256 = hashes.sha256 || "";

  const engine = item?.engine;
  const reason = item?.reason;
  const score = item?.score;
  const pid = item?.pid;
  const isActive =
    item?.is_active_threat === true || String(item?.is_active_threat).toLowerCase() === "true";

  const topThreat = Array.isArray(item?.threats) && item.threats[0] ? item.threats[0] : null;
  const mitre = topThreat?.mitre;
  const severity = topThreat?.severity || (topThreat?.level ? `L${topThreat.level}` : null);

  const defaultRows = [
    isProcess
      ? { label: "PID", value: pid }
      : { label: "Path", value: item?.file || item?.path, wrap: true },
    { label: "Reason", value: reason },
    { label: "MD5", value: md5, mono: true, wrap: true },
    { label: "SHA1", value: sha1, mono: true, wrap: true },
    { label: "SHA256", value: sha256, mono: true, wrap: true },
    { label: "Signature", value: item?.signature_status },
    severity ? { label: "Severity", value: severity ? severity.toUpperCase() : "None" } : null,
    Array.isArray(mitre) && mitre.length ? { label: "MITRE", value: mitre.join(" • ") } : null,
    Array.isArray(item?.threats && engine !== "sigma")
      ? { label: "Threats", value: item.threats.length }
      : null,
  ].filter(Boolean);

  const rows = Array.isArray(preview) ? preview : defaultRows;

  return (
    <div className="thunter-additional-data__content">
      {/* Header */}
      <div className="thunter-additional-data__content__header">
        <div className="thunter-additional-data__content__header-left">
          <div className="thunter-additional-data__content__icon" aria-hidden="true">
            {isProcess ? (
              <AttackMapFolderIcon width={26} height={26} />
            ) : (
              <FileIcon color="white" width={18} height={18} />
            )}
          </div>
          <h3 className="thunter-additional-data__content__title" title={title}>
            {title}
          </h3>
          <div className="thunter-additional-data__content__badges">
            {engine && <EngineBadge engine={engine} />}
            <SignatureBadge
              status={item?.signature_status}
              subject={item?.subject_name}
              issuer={item?.signer}
            />
            {isProcess && pid && <Pill kind="pid">PID: {pid}</Pill>}
            {typeof score !== "undefined" && (
              <Pill kind={score >= 20 ? "score-high" : score >= 10 ? "score-med" : "score-low"}>
                Score: {score}
              </Pill>
            )}
            {isActive && <Pill kind="active">Active</Pill>}
          </div>
        </div>

        <div className="thunter-additional-data__content__header-right">{rightHeader}</div>
      </div>

      {rows.length > 0 && (
        <div className="thunter-additional-data__content__mid">
          {rows.map((r, i) => (
            <KeyValueRow
              key={`${r.label}-${i}`}
              label={r.label}
              value={r.value}
              mono={r.mono}
              wrap={r.wrap}
            />
          ))}
        </div>
      )}

      <div className="thunter-additional-data__content__bottom">{children}</div>
    </div>
  );
}
